import { FunctionComponent } from 'react';

// Components
import SearchLayout from '@Components/Layout/Search';
import DoctorItem from '@Pages/tra-cuu/Components/DoctorItem';
import DownloadApp from '@Pages/bai-viet/Components/DownloadApp';
import MedicalSpecialties from '@Components/UI/MedicalSpecialties';
import SearchUI from '@Components/Layout/Search/Components/SearchUI';
import LayoutContainer from '@Components/Layout/Components/Container';
import Breadcrumb, { PathLevelProps } from '@Components/UI/Breadcrumb';
import EmptySearch from '@Components/Layout/Search/Components/EmptySearch';
import { useViewDetected, ViewMedia } from '@Components/Utils/ViewDetected';
import LayoutResult from '@Components/Layout/Search/Components/LayoutResult';

// Types
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

// Utilites
import classnames from 'classnames';
import doctorApi from '@Libs/api/user/doctor';
import { getServerSideHttp } from '@Libs/http';
import { getAttachmentAvatar } from '@Libs/utils';

// Styles
import styles from './Doctors.module.scss';

// Icons
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Contexts
import { useAppStore } from '@Components/AppStore/Context';
import { useLayoutContext } from '@Components/Layout/Contexts';
import { useMedicalSpecialites } from '@Components/UI/MedicalSpecialy';
import { useDoctorsManager, withDoctorsManager } from './Contexts/doctors';

// Type
import { useRouter } from 'next/router';
import { Doctor } from './[id]/DoctorPage';

const paths: Array<PathLevelProps> = [
    {
        label: 'Tra cứu',
        path: '/bai-viet',
    },
    {
        label: 'Kết quả tìm kiếm',
    },
];

interface DoctorsProps {
    keyword: string;
    doctors: Doctor[];
    filterSpecialty: string;
}

const LIMIT = 10;

const buildMedicalSpecialtyLink = (code: string) => {
    if (!code) {
        return '/tra-cuu/bac-si';
    }

    return `/tra-cuu/bac-si?chuyen_khoa=${code}`;
};

const Doctors: FunctionComponent<InferGetServerSidePropsType<typeof getServerSideProps> & DoctorsProps> = ({ keyword, filterSpecialty }) => {
    const { push } = useRouter();
    const { setLoading } = useAppStore();
    const { onScroll } = useLayoutContext();
    const { deviceType } = useViewDetected();
    const { parse } = useMedicalSpecialites();
    const { doctors, hasMore, loadMore } = useDoctorsManager();

    const isMobile = ['mobile', 'tablet'].includes(deviceType);

    onScroll(
        async (event, { isBottom }) => {
            if (isMobile && isBottom) {
                await handleLoadMore();
            }

            return () => setLoading(false);
        },
        [doctors],
        ({ isBottom }) => isMobile && isBottom && hasMore && setLoading(true),
    );

    const handleSearch = (keyword) => {
        push({
            pathname: '/tra-cuu/bac-si',
            query: {
                s: keyword,
                ...(filterSpecialty && { chuyen_khoa: filterSpecialty }),
            },
        });
    };

    const handleLoadMore = () => {
        loadMore(
            {
                ...(keyword && { keyword }),
                ...(filterSpecialty && { filterSpecialty }),
            },
            doctors.length,
        );
    };

    const renderBannerRight = () => {
        return (
            <>
                <ViewMedia is={'desktop'}>
                    <MedicalSpecialties className={styles.medical_specialties} buildLink={buildMedicalSpecialtyLink} activeCode={filterSpecialty} />

                    <DownloadApp className={styles.download_app} />
                </ViewMedia>
            </>
        );
    };

    const renderDoctors = () => {
        return doctors.map((doctor, index) => {
            return (
                <div className={styles.item} key={`doctor_item_${index}`}>
                    <DoctorItem
                        id={doctor.id}
                        className={styles.doctor}
                        title={doctor?.profile?.title}
                        rating={doctor?.profile?.rating}
                        fullName={doctor?.profile?.fullName}
                        avatar={getAttachmentAvatar(doctor?.id, 'doctor')}
                        specialist={!filterSpecialty && parse([doctor?.profile?.medicalSpecialties[0]], 'name')}
                        experience={doctor?.profile?.graduateYear && new Date().getFullYear() - Number(doctor?.profile?.graduateYear)}
                    />
                </div>
            );
        });
    };

    return (
        <SearchLayout className={classnames([styles.wrapper, styles[deviceType]])} onSearch={handleSearch} keyword={keyword}>
            <LayoutContainer className={styles.breadcrumb}>
                <Breadcrumb paths={paths} />
            </LayoutContainer>

            <ViewMedia is={'desktop'}>
                <div className={styles.search_wrapper}>
                    <SearchUI
                        keyword={keyword}
                        title="Tra cứu Bác sĩ"
                        onSearch={handleSearch}
                        className={styles.search}
                        titleClassName={styles?.title}
                        subTitleClassName={styles.sub_title}
                        subTitle={
                            <span>
                                Dưới đây là những <b>Bác sĩ</b> phù hợp với từ khóa của bạn.
                            </span>
                        }
                    />
                </div>
            </ViewMedia>

            <ViewMedia is={['tablet', 'mobile']}>
                <LayoutContainer className={styles.header}>
                    <h3 className={styles.title}>Tra cứu Bác sĩ</h3>
                    <div className={styles.text}>Dưới đây là những kết quả phù hợp nhất với từ khóa của bạn.</div>
                </LayoutContainer>

                <MedicalSpecialties className={styles.medical_specialties} buildLink={buildMedicalSpecialtyLink} activeCode={filterSpecialty} />
            </ViewMedia>

            <div className={styles.body}>
                <LayoutResult className={styles.layout_container} extendComponent={renderBannerRight()} hasExtendComponent={deviceType === 'desktop'} hasFormAdvisory={false}>
                    {doctors.length > 0 && (
                        <div className={styles.doctors}>
                            <h3 className={styles.title}>Danh sách kết quả</h3>

                            <div className={styles.list}>{renderDoctors()}</div>

                            <ViewMedia is={'desktop'}>
                                {hasMore && (
                                    <a className={styles.view_more} onClick={handleLoadMore}>
                                        Xem thêm bác sĩ <FontAwesomeIcon width={16} height={16} className={styles.icon_arrow} icon={faArrowDown} />
                                    </a>
                                )}
                            </ViewMedia>
                        </div>
                    )}
                    {doctors.length === 0 && (
                        <div className={styles.empty_wrapper}>
                            <EmptySearch withoutSearch title="Tra cứu Bác sĩ" src={'/assets/tra-cuu/img_search_empty.png'} subTitle="Xin lỗi chúng tôi không tìm thấy kết quả phù hợp với bạn." />
                        </div>
                    )}
                </LayoutResult>
            </div>
        </SearchLayout>
    );
};

export default withDoctorsManager<DoctorsProps>(Doctors)({ limit: LIMIT });

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const http = getServerSideHttp(ctx);
    const keyword: any = ctx?.query?.s || null;
    const filterSpecialty: any = ctx?.query?.chuyen_khoa || null;

    const { doctors = [] } = await doctorApi(http).search({
        ...(keyword && { q: keyword }),
        ...(filterSpecialty && { filterSpecialties: [filterSpecialty] }),
    });

    return {
        props: {
            doctors,
            keyword,
            filterSpecialty,
        },
    };
};
